/**
 * Wall-clock time in a time zone (daylight saving time included). Pure. The zone's UTC offset is looked up once per
 * UTC hour and cached: every zone used here (the markets' New York, London, Frankfurt, Tokyo; the zones times are shown
 * in, `timeZones.ts`) changes its offset only on a full UTC hour, so the cache is exact. The engine asks for every bar of long series, so the caches are keyed by numbers (no
 * string per call) and a local day's date text is built once.
 */

interface LocalTime {
  /** Calendar date "yyyy-mm-dd". */
  date: string;
  /** Minutes since local midnight. */
  minutes: number;
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number;
  /** The local calendar day as days since 1970. */
  day: number;
}

const HOUR = 3_600_000;
const DAY = 86_400_000;
/** Cached hours per zone before the cache starts over (bounded memory). */
const MAX_HOURS = 200_000;

/** A zone's formatter and its offsets per UTC hour. */
interface Zone {
  format: Intl.DateTimeFormat;
  offsets: Map<number, number>;
}
const zones = new Map<string, Zone>();
/** The date text per local day (days since 1970). */
const dates = new Map<number, string>();

function zoneOf(timeZone: string): Zone {
  const hit = zones.get(timeZone);
  if (hit) return hit;
  const zone = { format: new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" }), offsets: new Map<number, number>() };
  zones.set(timeZone, zone);
  return zone;
}

/** The zone's offset at the UTC hour (milliseconds). */
function hourOffset(zone: Zone, hour: number): number {
  const p = Object.fromEntries(zone.format.formatToParts(new Date(hour * HOUR)).map((x) => [x.type, Number(x.value)]));
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - hour * HOUR;
}

function offsetOf(t: number, timeZone: string): number {
  const zone = zoneOf(timeZone);
  const hour = Math.floor(t / HOUR);
  const cached = zone.offsets.get(hour);
  if (cached !== undefined) return cached;
  const off = hourOffset(zone, hour);
  if (zone.offsets.size > MAX_HOURS) zone.offsets.clear();
  zone.offsets.set(hour, off);
  return off;
}

/** The zone's offset from UTC at time t (milliseconds; New York in summer −4 h). */
export const utcOffset = (t: number, timeZone: string): number => offsetOf(t, timeZone);

/** "yyyy-mm-dd" of a local day (days since 1970). */
function dateOf(day: number): string {
  const hit = dates.get(day);
  if (hit !== undefined) return hit;
  const d = new Date(day * DAY);
  const text = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  dates.set(day, text);
  return text;
}

/** The local date, minute of the day and weekday of time t in a time zone. */
export function localTime(t: number, timeZone: string): LocalTime {
  const local = t + offsetOf(t, timeZone);
  const day = Math.floor(local / DAY);
  const minutes = Math.floor((local - day * DAY) / 60_000);
  // 1 January 1970 was a Thursday
  return { date: dateOf(day), minutes, weekday: (((day + 4) % 7) + 7) % 7, day };
}

/** The UTC time of a wall-clock time in a time zone (the wall time given as if it were UTC); in the hour skipped by
 * daylight saving time the offset after the change applies. */
export function wallToUtc(wall: number, timeZone: string): number {
  return wall - offsetOf(wall - offsetOf(wall, timeZone), timeZone);
}
